import type { ChangeEvent, DragEvent } from 'react';
import { useState } from 'react';
import { getPostArtifact, isDataTransfer } from '@utils/fileUtils';
import type { PostArtifact } from '@models/note';

export type FileEntry = {
  artifact: PostArtifact;
  documentTypeId: string;
  description: string;
};

export const useDocumentFileSelection = (defaultDocumentTypeId: string) => {
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [dragging, setDragging] = useState(false);

  const addFiles = (newFiles: File[]) => {
    const entries: FileEntry[] = newFiles.map((file) => {
      const [artifact] = getPostArtifact(file);
      return { artifact, documentTypeId: defaultDocumentTypeId, description: '' };
    });
    setFiles((prev) => [...prev, ...entries]);
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files) {
      addFiles(Array.from(e.target.files));
      (e.target as HTMLInputElement).value = '';
    }
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const droppedFiles: File[] = [];
    for (let i = 0; i < e.dataTransfer.items.length; i++) {
      if (e.dataTransfer.items[i].kind === 'file') {
        const file = e.dataTransfer.items[i].getAsFile();
        if (file) droppedFiles.push(file);
      }
    }
    addFiles(droppedFiles);
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDataTransfer(e)) setDragging(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const updateEntry = (index: number, updates: Partial<Pick<FileEntry, 'documentTypeId' | 'description'>>) => {
    setFiles((prev) => prev.map((entry, i) => (i === index ? { ...entry, ...updates } : entry)));
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const reset = () => setFiles([]);

  const totalSize = files.reduce((sum, f) => sum + f.artifact.size, 0);
  const validFiles = files.filter((f) => !f.artifact.invalidMsg && f.documentTypeId !== '');

  return {
    files,
    dragging,
    handleFileInputChange,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    updateEntry,
    removeFile,
    reset,
    totalSize,
    validFiles,
  };
};
